import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MessageSquare, AlertTriangle, CheckCircle2, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";

const RETRY_ROLES = new Set(["admin", "manager", "service_head"]);

const PROVIDER_LABEL = {
  twilio_whatsapp: "Twilio WhatsApp",
  twilio_sms: "Twilio SMS",
  mock: "Mock (no provider)",
};

export default function Notifications() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [retrying, setRetrying] = useState({});

  const load = () => api.get("/notifications", { params: { limit: 500 } }).then((r) => setList(r.data));
  const loadStatus = () => api.get("/notifications/status")
    .then((r) => { setStatus(r.data); setStatusError(null); })
    .catch((e) => {
      const code = e.response?.status;
      setStatusError({ kind: code === 401 || code === 403 ? "auth" : "other",
        message: code === 401 || code === 403
          ? "Please log in again to view notification provider status."
          : `Could not fetch provider status (HTTP ${code ?? "network error"}).`,
      });
      setStatus(null);
    });

  useEffect(() => { load(); loadStatus(); }, []);

  const markAll = async () => { await api.post("/notifications/mark-all-read"); load(); };

  const canRetry = user && RETRY_ROLES.has(user.role);

  const retryOne = async (nid) => {
    setRetrying((p) => ({ ...p, [nid]: true }));
    try {
      const { data } = await api.post(`/notifications/${nid}/retry`);
      if (data.ok) toast.success(`${PROVIDER_LABEL[data.provider] || "Message"} sent`);
      else toast.error("Retry failed", { description: data.twilio_error_message || data.error || "Provider rejected the request" });
      await load();
    } catch (e) {
      toast.error("Retry failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setRetrying((p) => ({ ...p, [nid]: false })); }
  };

  const filtered = filter === "all" ? list
    : filter === "failed" ? list.filter((n) => n.status === "failed")
    : list.filter((n) => n.channel === filter);

  // Provider-aware status flags
  const provider = status?.provider || "twilio_whatsapp";
  const providerLabel = PROVIDER_LABEL[provider] || provider;
  const isWA = provider === "twilio_whatsapp";
  const isSMS = provider === "twilio_sms";
  const isLive = status?.configured === true && status?.mode === "live";
  const credsInvalid = status?.credentials_valid === false && status?.credentials_present;
  const acctSuspended = status?.account_status && status?.account_status !== "active";
  const waMissingFrom = isWA && status?.credentials_valid && !status?.whatsapp_from_set;
  const isSandbox = isWA && status?.whatsapp_from_is_sandbox;

  const channelLabel = isWA ? "WhatsApp" : isSMS ? "SMS" : "Mock";
  const failedCount = list.filter((n) => n.status === "failed").length;

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-500 mt-1">
            Provider: <span className="font-semibold text-slate-900" data-testid="provider-label">{providerLabel}</span>
            {" · "}Channel: <span className="font-semibold text-slate-900" data-testid="channel-label">{channelLabel}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { loadStatus(); load(); }} data-testid="refresh-status-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button variant="outline" onClick={markAll} data-testid="mark-all-btn">Mark all read</Button>
        </div>
      </div>

      {statusError ? (
        <div className="bg-rose-50 border border-rose-200 rounded-md p-3 flex items-start gap-2" data-testid="status-error-banner">
          <AlertTriangle size={16} className="text-rose-700 mt-0.5" />
          <div className="text-sm text-rose-900">{statusError.message}</div>
        </div>
      ) : isLive && isWA ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2" data-testid="mode-banner-live">
          <CheckCircle2 size={16} className="text-emerald-700 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <span className="font-semibold">WhatsApp Live:</span> Twilio account is active and a WhatsApp sender is configured. Outgoing messages will be delivered.
            <span className="ml-2 font-mono text-[10px] bg-emerald-100 px-1 rounded">from={status?.whatsapp_from_preview}</span>
            <span className="ml-1 font-mono text-[10px] bg-emerald-100 px-1 rounded">country={status?.default_country_code}</span>
          </div>
        </div>
      ) : isLive && isSMS ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2" data-testid="mode-banner-live-sms">
          <CheckCircle2 size={16} className="text-emerald-700 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <span className="font-semibold">SMS Live:</span> Twilio account is active. SMS messages will be delivered.
          </div>
        </div>
      ) : credsInvalid || acctSuspended ? (
        <div className="bg-rose-50 border border-rose-200 rounded-md p-3 flex items-start gap-2" data-testid="mode-banner-creds-invalid">
          <AlertTriangle size={16} className="text-rose-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-rose-900 flex-1">
            <div className="font-semibold">
              {acctSuspended ? `Twilio account is ${status?.account_status}.` : "Twilio credentials are invalid."}
            </div>
            <div className="mt-1">
              {status?.twilio_error_message || "Live verification failed. Please check Account SID / Auth Token."}
              {status?.twilio_error_code && (
                <span className="ml-2 font-mono text-[10px] bg-rose-100 px-1 rounded" data-testid="twilio-error-code">
                  code={status.twilio_error_code}
                </span>
              )}
            </div>
            <div className="mt-1 text-[11px] text-rose-700">
              All new outgoing {channelLabel} messages will fail until this is fixed. Use the Retry button once corrected.
            </div>
          </div>
        </div>
      ) : waMissingFrom ? (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2" data-testid="mode-banner-wa-missing-from">
          <AlertTriangle size={16} className="text-amber-700 mt-0.5" />
          <div className="text-sm text-amber-900">
            <span className="font-semibold">WhatsApp sender not configured.</span>{" "}
            Set <code className="font-mono bg-amber-100 px-1 rounded">TWILIO_WHATSAPP_FROM</code> in the environment to enable WhatsApp delivery.
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2" data-testid="mode-banner-mock">
          <AlertTriangle size={16} className="text-amber-700 mt-0.5" />
          <div className="text-sm text-amber-900">
            <span className="font-semibold">MOCKED:</span> {providerLabel} is not yet active. Messages below are previews of what will be sent once the provider is configured.
          </div>
        </div>
      )}

      {isSandbox && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-2" data-testid="sandbox-banner">
          <Info size={16} className="text-blue-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <span className="font-semibold">Twilio WhatsApp Sandbox active.</span>{" "}
            Only numbers that have <span className="font-mono bg-blue-100 px-1 rounded">join &lt;sandbox-keyword&gt;</span>{"'"}d the sandbox can receive messages. Move to an approved WhatsApp sender for production.
          </div>
        </div>
      )}

      {isWA && status?.content_templates && (
        <Card data-testid="content-templates-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={14} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-800">WhatsApp Content Templates</span>
              <span className="text-[11px] text-slate-500">
                Business-initiated WhatsApp requires approved templates (Twilio error 63016 without them).
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(status.content_templates).map(([key, meta]) => (
                <div key={key}
                     className={`flex items-center justify-between rounded-md border p-2 text-xs ${meta.set ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}
                     data-testid={`tpl-${key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] text-slate-700 truncate">{meta.env_var}</div>
                    <div className="text-[10px] text-slate-500">event: {key}</div>
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    {meta.set ? (
                      <span className="text-emerald-700 font-mono text-[11px]" data-testid={`tpl-${key}-sid`}>{meta.sid_preview}</span>
                    ) : (
                      <span className="text-rose-700 font-semibold text-[11px]">NOT SET</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 flex-wrap">
        {[
          { k: "all", label: `ALL (${list.length})` },
          { k: "whatsapp", label: `WHATSAPP (${list.filter((n) => n.channel === "whatsapp").length})` },
          { k: "sms", label: `SMS (${list.filter((n) => n.channel === "sms").length})` },
          { k: "failed", label: `FAILED (${failedCount})` },
        ].map((f) => (
          <Button key={f.k}
                  variant={filter === f.k ? "default" : "outline"}
                  className={filter === f.k ? "bg-slate-900 text-white" : ""}
                  onClick={() => setFilter(f.k)}
                  data-testid={`filter-${f.k}`}>
            {f.label}
          </Button>
        ))}
      </div>

      <Card><CardContent className="p-0">
        <div className="divide-y divide-slate-100">
          {filtered.length === 0 && <div className="p-12 text-center text-slate-400 text-sm">No notifications.</div>}
          {filtered.map((n) => {
            const isFailed = n.status === "failed";
            const chBg = n.channel === "whatsapp" ? "bg-green-100 text-green-700"
                        : n.channel === "sms" ? "bg-blue-100 text-blue-700"
                        : "bg-slate-100 text-slate-600";
            const toShow = n.channel === "whatsapp" ? (n.to_whatsapp || n.to_e164 || n.to) : (n.to_e164 || n.to);
            return (
              <div key={n.id} className={`p-4 flex items-start gap-3 ${!n.is_read ? "bg-blue-50/40" : ""}`} data-testid={`notif-row-${n.id}`}>
                <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${isFailed ? "bg-rose-100 text-rose-700" : chBg}`}>
                  <MessageSquare size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-semibold">{n.channel || "—"}</Badge>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-semibold">→ {n.recipient_role}</Badge>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${n.status === "sent" ? "text-emerald-700" : isFailed ? "text-rose-700" : "text-amber-700"}`}
                          data-testid={`notif-status-${n.id}`}>{n.status}</span>
                    {n.twilio_error_code && (
                      <span className="text-[10px] font-mono bg-rose-50 text-rose-700 px-1 rounded" data-testid={`notif-error-code-${n.id}`}>
                        code={n.twilio_error_code}
                      </span>
                    )}
                    {(n.twilio_message_sid || n.provider_id) && (
                      <span className="text-[10px] font-mono text-slate-400" data-testid={`notif-sid-${n.id}`}>
                        {(n.twilio_message_sid || n.provider_id).slice(-8)}
                      </span>
                    )}
                    {n.complaint_number && (
                      <Link to={`/complaints/${n.complaint_id}`} className="font-mono text-xs text-blue-600 hover:underline">
                        {n.complaint_number}
                      </Link>
                    )}
                    <span className="ml-auto text-[11px] text-slate-400 font-mono">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-slate-900 mt-1">{n.message}</div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">to: {toShow}{n.to && toShow !== n.to && <span className="text-slate-400"> (from raw {n.to})</span>}</div>
                  {(n.twilio_error_message || n.error_message) && (
                    <div className="text-xs text-rose-700 mt-0.5" data-testid={`notif-error-${n.id}`}>
                      <span className="font-semibold">Twilio error:</span> {n.twilio_error_message || n.error_message}
                    </div>
                  )}
                  {(n.content_sid || n.content_template_env) && (
                    <div className="text-[11px] text-slate-500 mt-0.5 font-mono" data-testid={`notif-tpl-${n.id}`}>
                      <span className="font-semibold text-slate-700 font-sans">Template:</span>{" "}
                      {n.content_template_env && <span>{n.content_template_env} → </span>}
                      {n.content_sid
                        ? <span className="text-slate-800">{n.content_sid}</span>
                        : <span className="text-rose-600 font-sans">not set</span>}
                    </div>
                  )}
                  {n.retried_at && (
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Last retry: {new Date(n.retried_at).toLocaleString()}{n.retried_by ? ` by ${n.retried_by}` : ""}
                    </div>
                  )}
                  {isFailed && canRetry && (
                    <div className="mt-2">
                      <Button size="sm" variant="outline"
                              onClick={() => retryOne(n.id)}
                              disabled={!!retrying[n.id]}
                              data-testid={`retry-${n.channel || "msg"}-${n.id}`}>
                        <RefreshCw size={12} className={`mr-1 ${retrying[n.id] ? "animate-spin" : ""}`} />
                        {retrying[n.id] ? "Retrying…" : `Retry ${channelLabel}`}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent></Card>
    </div>
  );
}
